// components/private/paket/ParticipantPackageForm.tsx
"use client";

import { useEffect, useMemo, useState } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import ActivitySelector from "@/components/private/ActivitySelector";
import StepNavigation from "@/components/private/StepNavigation";
import { axiosClient } from "@/lib/axiosClient";

import { usePackageTree } from "@/hooks/usePackageTree";
import { useRegistrationCheck } from "@/hooks/useRegistrationCheck";
import { useRegistrationDetail } from "@/hooks/useRegistrationDetail";

import { Card } from "@/components/ui/card";
import ProgressIndicator from "@/components/private/ProgressIndicator";

import type { PackageItem, RegistrationItem, RegistrationResponse } from "@/types/registration";
import InfoKonvensiFees from "./InfoKonvensiFees";
import CityTourSelector from "../CityTourSelector";

/** Local / auxiliary types */
type PackageTreeItem = PackageItem & {
    children?: PackageTreeItem[];
};

type RegistrationCheck = {
    registered: boolean;
    registration_id?: number;
};

type Props = {
    user: { participant_id: number; role_id: number };
    activePeriod: { id: number };
    autoEnable?: boolean;
};

export default function ParticipantPackageForm({ user, activePeriod, autoEnable = true }: Props) {
    const queryClient = useQueryClient();

    const [selectedParent, setSelectedParent] = useState<number | null>(null);
    const [selectedChild, setSelectedChild] = useState<number | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const enabled = Boolean(autoEnable && user?.participant_id && activePeriod?.id);

    // Hooks (we don't assume their internal types; cast their .data safely below)
    const packageTreeQuery = usePackageTree(activePeriod?.id ?? null, enabled);
    const registrationCheckQuery = useRegistrationCheck(user?.participant_id ?? null, activePeriod?.id ?? null, enabled);
    const registrationDetailQuery = useRegistrationDetail(user?.participant_id ?? null, activePeriod?.id ?? null, enabled && Boolean(registrationCheckQuery.data?.registered));

    // cast data from hooks into typed shapes
    const packageTree = packageTreeQuery.data as PackageTreeItem[] | undefined;
    const loadingPackages = packageTreeQuery.isLoading;
    const errorPackages = packageTreeQuery.isError;

    const registrationCheck = registrationCheckQuery.data as RegistrationCheck | undefined;
    const loadingCheck = registrationCheckQuery.isLoading;

    const isRegistered = Boolean(registrationCheck?.registered);
    const registrationIdFromCheck = registrationCheck?.registration_id ?? null;

    const registration = registrationDetailQuery.data as RegistrationItem | undefined;
    const loadingRegistration = registrationDetailQuery.isLoading;

    const cityTourPackage = useMemo(() => packageTree?.find((p) => p.code === "PKT-CITY"), [packageTree]);

    /** Sync selection from backend registration (when registration detail available) */
    useEffect(() => {
        if (!packageTree) return;

        const pkgIdFromDetail = registration?.package_id ?? null;
        const pkgId = pkgIdFromDetail ?? null;

        if (!pkgId) return;

        const parent = packageTree.find((p) => p.id === pkgId);

        if (parent) {
            setSelectedParent(parent.id);
            setSelectedChild(null);
            return;
        }

        const sportParent = packageTree.find((p) => p.children?.some((c) => c.id === pkgId));

        if (sportParent) {
            setSelectedParent(sportParent.id);
            setSelectedChild(pkgId);
        }

        const cityTourParent = packageTree.find((p) => p.children?.some((c) => c.id === pkgId));

        if (cityTourParent) {
            setSelectedParent(cityTourParent.id);
            setSelectedChild(pkgId);
        }
    }, [registration, packageTree]);

    /** Update Step API */
    const updateStep = async (step: number) => {
        try {
            await axiosClient.put(`/participants/${user.participant_id}/registrations/step/${activePeriod.id}`, { current_step: step });

            await queryClient.invalidateQueries({
                queryKey: ["participant-registrations", user.participant_id, activePeriod.id],
            });

            await queryClient.invalidateQueries({
                queryKey: ["registration-check", user.participant_id, activePeriod.id],
            });
        } catch (err: unknown) {
            console.error("updateStep error:", err);
            toast.error("Gagal memperbarui langkah.");
        }
    };

    /**
     * Save Registration (create or update)
     *
     * Strategy:
     * - Jika registrationCheck menyatakan sudah registered dan punya registration_id -> PUT
     * - Jika tidak terdaftar -> Coba POST
     *   - Jika POST error karena already registered -> refresh check lalu PUT
     */
    const saveRegistration = async ({ package_id }: { package_id: number }): Promise<RegistrationResponse> => {
        if (!activePeriod?.id || !user?.participant_id) {
            throw new Error("Periode atau user tidak valid");
        }

        const payload = {
            period_id: activePeriod.id,
            package_id,
            note: "Update paket peserta",
        };

        // Case A: already registered according to lightweight check => update
        if (registrationCheck?.registered && registrationCheck?.registration_id) {
            const res = await axiosClient.put<RegistrationResponse>(
                `/participants/${user.participant_id}/registrations/${registrationCheck.registration_id}`,
                payload
            );
            return res.data;
        }

        // Case B: not registered according to check -> try create
        try {
            const res = await axiosClient.post<RegistrationResponse>(
                `/participants/${user.participant_id}/registrations`,
                payload
            );
            return res.data;
        } catch (err: unknown) {
            // Parse error safely
            const axiosErr = err as { response?: { data?: unknown }; message?: string };
            const respData = axiosErr.response?.data as { message?: string } | undefined;
            const serverMessage = respData?.message ?? axiosErr.message ?? "";

            const alreadyRegistered = typeof serverMessage === "string" && serverMessage.toLowerCase().includes("already registered");

            if (alreadyRegistered) {
                // Refresh check to obtain registration_id if backend already created it
                await queryClient.invalidateQueries({
                    queryKey: ["registration-check", user.participant_id, activePeriod.id],
                });

                const latestCheck = queryClient.getQueryData<RegistrationCheck>([
                    "registration-check",
                    user.participant_id,
                    activePeriod.id,
                ]);

                const rid = latestCheck?.registration_id ?? registrationIdFromCheck;

                if (rid) {
                    const res = await axiosClient.put<RegistrationResponse>(
                        `/participants/${user.participant_id}/registrations/${rid}`,
                        payload
                    );
                    return res.data;
                }
            }

            // Not recoverable -> rethrow
            throw err;
        }
    };

    /** React Query mutation */
    const saveMutation = useMutation<RegistrationResponse, unknown, { package_id: number }>({
        mutationFn: saveRegistration,
        onMutate: () => setIsSaving(true),

        onSuccess: async (result) => {
            toast.success("Paket berhasil disimpan!");

            // result.data is RegistrationItem | null (per your types)
            const registrationId = result?.data?.registration_id ?? null;

            if (!registrationId) {
                console.warn("saveMutation: registration_id not found in response", result);
            }

            // update step to 2
            await updateStep(2);

            // refresh relevant queries
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: ["participant-registrations", user.participant_id, activePeriod.id],
                }),
                queryClient.invalidateQueries({
                    queryKey: ["registration-check", user.participant_id, activePeriod.id],
                }),
                queryClient.invalidateQueries({
                    queryKey: ["registration-detail", user.participant_id, activePeriod.id],
                }),
                queryClient.invalidateQueries({
                    queryKey: ["registration-step", user.participant_id, activePeriod.id],
                }),
            ]);
        },

        onError: (err: unknown) => {
            console.error("Mutation error:", err);

            const axiosErr = err as { response?: { data?: { message?: string } }; message?: string } | undefined;
            const serverMessage = axiosErr?.response?.data?.message ?? axiosErr?.message;

            if (serverMessage && typeof serverMessage === "string") {
                toast.error(serverMessage);
            } else {
                toast.error("Gagal menyimpan paket.");
            }
        },

        onSettled: () => setIsSaving(false),
    });

    /** NEXT */
    const handleNext = async () => {
        if (isSaving || saveMutation.isPending) {
            console.log("Next disabled: still saving...");
            return;
        }

        if (!selectedParent) {
            toast.error("Pilih paket dulu!");
            return;
        }

        if (loadingCheck) {
            toast.error("Sedang memeriksa status pendaftaran...");
            return;
        }

        if (selectedParent === cityTourPackage?.id && !selectedChild) {
            toast.error("Pilih city toure!");
            return;
        }

        const packageId = selectedChild ?? selectedParent;

        try {
            await saveMutation.mutateAsync({ package_id: packageId as number });
        } catch (err) {
            console.error("handleNext error:", err);
        }
    };


    /** BACK */
    const handleBack = async () => {
        await updateStep(1);
    };

    const isLoadingAll = loadingPackages || loadingCheck || (isRegistered && loadingRegistration);

    /** LOADING UI */
    if (isLoadingAll) {
        return (
            <>
                <div className="mx-auto py-12 space-y-10 pt-0 max-w-6xl">
                    <Card className="border-0 shadow-lg bg-white dark:bg-gray-800 mb-3">
                        <div className="text-center space-y-2 py-4">
                            <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200">
                                Memuat daftar paket kegiatan...
                            </h2>
                            <p className="text-gray-500 dark:text-gray-400">
                                Mohon tunggu, sedang mengambil data paket.
                            </p>
                        </div>
                    </Card>
                    <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className="animate-pulse overflow-hidden rounded-2xl bg-gradient-to-br from-gray-200/60 to-gray-100/30 dark:from-gray-700/40 dark:to-gray-800/50"
                            >
                                <div className="h-48 w-full bg-gray-300 dark:bg-gray-700" />
                                <div className="p-5 space-y-3">
                                    <div className="h-5 w-2/3 bg-gray-300 dark:bg-gray-600 rounded" />
                                    <div className="h-4 w-full bg-gray-300 dark:bg-gray-700 rounded" />
                                    <div className="h-4 w-5/6 bg-gray-300 dark:bg-gray-700 rounded" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </>
        );
    }

    if (errorPackages) {
        return <div className="text-center text-red-500 py-10">Gagal memuat paket.</div>;
    }

    /** RENDER */
    return (
        <div className="space-y-6">
            <Card className="relative border-0 shadow-lg bg-white dark:bg-gray-800 mb-6 overflow-hidden rounded-2xl transition-all duration-300">
                <img src="/ornamen/ukiran-corner.png" alt="Ukiran Bali" className="absolute top-0 left-0 w-40 md:w-48 opacity-40 pointer-events-none z-0" />
                <img src="/ornamen/ukiran-corner.png" alt="Ukiran Bali" className="absolute top-0 right-0 w-40 md:w-48 scale-x-[-1] opacity-40 pointer-events-none z-0" />
                {/* Dot Pattern Layer */}
                <div
                    className="absolute inset-0 opacity-[0.1] dark:opacity-[0.05] pointer-events-none"
                    style={{
                        backgroundImage: `radial-gradient(currentColor 0.8px, transparent 0.8px)`,
                        backgroundSize: '12px 12px',
                    }}
                />

                {/* Glow Blur Background Central */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-32 w-64 bg-blue-500/10 dark:bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />

                {/* Content Area */}
                <div className="relative z-10 px-16 py-8 flex flex-col items-center text-center space-y-3">
                    <div className="space-y-1">
                        <h2 className="text-xl font-black tracking-tight text-slate-900 dark:text-white flex items-center justify-center gap-2 uppercase">
                            {user.role_id === 2 ? "Pilih Paket Kegiatan" : "Import Data Paket"}
                        </h2>

                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 leading-relaxed italic max-w-sm mx-auto">
                            {user.role_id === 2
                                ? "Tentukan pengalaman terbaik Anda melalui pilihan paket kegiatan kami."
                                : "Kelola data peserta secara efisien melalui unggahan berkas Excel kolektif."}
                        </p>
                    </div>
                </div>
            </Card>

            <ProgressIndicator currentStep={1} />

            {isSaving && (
                <div className="flex items-center justify-center gap-2 py-3 bg-blue-50 dark:bg-blue-900/30">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Menyimpan pilihan paket...
                </div>
            )}

            <ActivitySelector
                selected={selectedParent}
                onChange={(id: number) => {
                    setSelectedParent(id);
                    setSelectedChild(null);
                }}
                data={packageTree ?? []}
                periodId={activePeriod.id}
                participantId={user.participant_id}
            />

            {selectedParent === cityTourPackage?.id && cityTourPackage?.children && (
                <CityTourSelector
                    selected={selectedChild}
                    onChange={setSelectedChild}
                    data={cityTourPackage?.children ?? []} 
                    periodId={activePeriod.id}
                    participantId={user.participant_id}
                    parentName={cityTourPackage?.name}
                />
            )}

            <InfoKonvensiFees />

            <StepNavigation
                onNext={handleNext}
                onBack={handleBack}
                nextDisabled={
                    isSaving ||                          // tetap disable saat saving
                    saveMutation.isPending ||            // disable saat mutation berjalan
                    loadingCheck ||                      // disable jika registration-check masih memuat
                    !selectedParent ||                   // harus pilih paket
                    (
                        (packageTree?.find(p => p.id === selectedParent)?.children?.length ?? 0) > 0 &&
                        !selectedChild                   // wajib pilih cabang olahraga
                    ) ||
                    (isRegistered && loadingRegistration) // sedang ambil detail → jangan next
                }
            />

        </div>
    );
}
